// deps
import React, { useMemo } from "react";
import classNames from "classnames";

// interface
import { ICardPaymentGroup } from "@payment-ui/interfaces/Card";

// components
import PaymentGroupContainer from "../components/Container";
import PaymentGroupTitle from "../components/Title";
import Card from "@payment-ui/components/payment-method/card";
import NewCard from "@payment-ui/components/payment-method/new-card";

//helpers
import { isEmpty } from "@swiggy-private/payment-core";
import {
    isAddNewCardPayment,
    isAddNewFoodCardPayment,
    isFoodCardGroup as isFoodCardPayment,
} from "@payment-ui/helpers/Card";
import {
    isPaymentMethodSelected,
    generatePaymentMethodId,
    getPaymentGroupDisplayName,
    getPaymentMethods,
    getPaymentGroupBannerMessage,
    isPaymentGroupEnabled,
    getPaymentGroupDisableMessage,
    getPaymentGroupIconUrl,
} from "@payment-ui/helpers/Payment";

// test-ids
import { PAYMENT_GROUP_TEST_IDS } from "@swiggy-private/payment-test-ids";
import styles from "../PaymentGroup.v4.scss";

export default function CardPaymentGroup(props: ICardPaymentGroup): React.ReactElement | null {
    const { isFrictionPage, groupTitle, payCtaText, payCtaClassName } = props;
    const paymentMethods = useMemo(() => getPaymentMethods(props), [props]);
    const isGroupEnabled = useMemo(() => isPaymentGroupEnabled(props.paymentData), [props.paymentData]);
    const [groupTitleDataTestId, bannerMessageTestId]: [string, string] = useMemo(
        () =>
            isFoodCardPayment(props.paymentData)
                ? [PAYMENT_GROUP_TEST_IDS.TITLE.FOOD_CARD, PAYMENT_GROUP_TEST_IDS.BANNER_MESSAGE.FOOD_CARD]
                : [PAYMENT_GROUP_TEST_IDS.TITLE.CARD, PAYMENT_GROUP_TEST_IDS.BANNER_MESSAGE.CARD],
        [props.paymentData],
    );

    const title = groupTitle ?? getPaymentGroupDisplayName(props.paymentData);

    const paymentGroupContainerClass = React.useMemo(
        () => classNames(styles.paymentGroupContainer, props.cardGroupCotainerClassName),
        [props.cardGroupCotainerClassName],
    );

    if (isEmpty(paymentMethods)) {
        return null;
    }

    return (
        <PaymentGroupContainer isFrictionPage={isFrictionPage}>
            {!props.hideGroupTitle && (
                <PaymentGroupTitle
                    title={title}
                    bannerMessage={getPaymentGroupBannerMessage(props.paymentData)}
                    disableMessage={getPaymentGroupDisableMessage(props.paymentData)}
                    data-testid={groupTitleDataTestId}
                    bannerMessageTestId={bannerMessageTestId}
                    groupIcon={getPaymentGroupIconUrl(props.paymentData)}
                />
            )}
            <div className={paymentGroupContainerClass}>
                {paymentMethods.map((paymentMethod) => {
                    const id = generatePaymentMethodId(paymentMethod);
                    if (isAddNewCardPayment(paymentMethod) || isAddNewFoodCardPayment(paymentMethod)) {
                        return (
                            <NewCard
                                key={paymentMethod.displayName}
                                id={id}
                                methodData={paymentMethod}
                                paymentActions={props.paymentActions}
                                selected={isPaymentMethodSelected(props.selectedPaymentId, id)}
                                options={props.options}
                                isPaymentGroupEnabled={isGroupEnabled}
                                nonTokenizeCardRemovalMsg={props.juspayMeta?.nonTokenizeCardRemovalMsg}
                            />
                        );
                    }

                    return (
                        <Card
                            key={paymentMethod.displayName}
                            id={id}
                            methodData={paymentMethod}
                            paymentActions={props.paymentActions}
                            isPaymentGroupEnabled={isGroupEnabled}
                            selected={isPaymentMethodSelected(props.selectedPaymentId, id)}
                            paymentAmount={props.paymentAmount}
                            juspayTokenizationMeta={props.juspayMeta?.tokenizationMeta}
                            payCtaText={payCtaText}
                            payCtaClassName={payCtaClassName}
                        />
                    );
                })}
            </div>
        </PaymentGroupContainer>
    );
}
